import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Smartphone, Loader2, LogOut, RefreshCw, Clock, Globe } from 'lucide-react';
import { useAuth, API } from '../../App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

function formatRelative(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return '—';
  }
}

/**
 * Admin-only dialog listing active device sessions for a single user.
 * Allows revoking a single session ("Disconnect") or all sessions at once.
 */
export default function UserDevicesDialog({ user, open, onOpenChange }) {
  const { token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const fetchSessions = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/${user.id}/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [user?.id, token]);

  useEffect(() => {
    if (open) fetchSessions();
  }, [open, fetchSessions]);

  const revokeOne = async (sid) => {
    setBusyId(sid);
    try {
      await axios.delete(`${API}/sessions/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Device disconnected');
      await fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setBusyId(null);
    }
  };

  const revokeAll = async () => {
    if (sessions.length === 0) return;
    if (!window.confirm(`Disconnect all ${sessions.length} active device${sessions.length !== 1 ? 's' : ''} for ${user.name}?`)) return;
    setRevokingAll(true);
    try {
      const res = await axios.delete(`${API}/users/${user.id}/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Disconnected ${res.data?.revoked_count ?? 0} device(s)`);
      await fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect all');
    } finally {
      setRevokingAll(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="user-devices-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-slate-700" />
            Active Devices — {user?.name}
          </DialogTitle>
          <DialogDescription>
            Devices currently logged in for this user. Disconnect any device to force immediate logout.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : sessions.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500" data-testid="no-active-devices">
              No active devices for this user.
            </div>
          ) : (
            sessions.map((s) => (
              <div
                key={s.id}
                className="border border-slate-200 rounded-md p-3 bg-white flex items-start justify-between gap-3"
                data-testid={`session-row-${s.id}`}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2 bg-slate-100 rounded-md">
                    <Smartphone className="h-4 w-4 text-slate-700" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{s.device_name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {formatRelative(s.last_seen)}</span>
                      <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> {s.ip || '—'}</span>
                    </div>
                    {s.user_agent && (
                      <div className="text-[10px] text-slate-400 mt-1 truncate max-w-[18rem]" title={s.user_agent}>
                        {s.user_agent}
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => revokeOne(s.id)}
                  disabled={busyId === s.id}
                  variant="ghost"
                  size="sm"
                  className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 flex-shrink-0"
                  data-testid={`disconnect-session-${s.id}`}
                >
                  {busyId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="h-4 w-4 mr-1" />Disconnect</>}
                </Button>
              </div>
            ))
          )}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between sm:items-center">
          <Button
            onClick={fetchSessions}
            variant="ghost"
            size="sm"
            disabled={loading}
            className="text-slate-600"
            data-testid="refresh-sessions"
          >
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button
            onClick={revokeAll}
            disabled={revokingAll || sessions.length === 0}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="disconnect-all-sessions"
          >
            {revokingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="h-4 w-4 mr-1" />Disconnect All</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
